/**
 * hydrus-variant-reactivity.js
 *
 * Client-side variant-picker reactivity for Hydrus PDPs using Shopify's
 * Section Rendering API. When a customer changes the selected variant via
 * radio-input option pickers, this script fetches the section's rendered
 * HTML for the new variant and swaps the inventory-state + ATC region
 * in-place — no full page reload.
 *
 * Consumer pattern (Liquid, inside a PDP section):
 *
 *   {%- assign variant_map = '{}' -%}
 *   <script>
 *     window.HydrusVariantReactivity.init({
 *       sectionId: '{{ section.id }}',
 *       productUrl: '{{ product.url }}',
 *       reactiveZoneId: 'simple-reactive-{{ section.id }}',
 *       variantMap: { {%- for v in product.variants -%}
 *         {{ v.title | json }}: {{ v.id }}{%- unless forloop.last -%},{%- endunless -%}
 *       {%- endfor -%} }
 *     });
 *   </script>
 *
 * Liquid structural requirements:
 * - The section's reactive region must have id={{ reactiveZoneId }} wrapping
 *   the hidden variant id input + inventory-state render + ATC button.
 * - The variant-picker radio inputs must be inside a <form> that contains
 *   the reactive zone (standard Shopify {%- form 'product' -%} pattern).
 * - Radio input names must start with "options[" (Shopify convention).
 *
 * Shared between simple buybox (sections/hydrus-pdp-simple-buybox.liquid)
 * and future rich buybox variant picker (PENDING-ITEMS #30).
 *
 * On fetch failure, the hidden variant id input is still updated (so ATC
 * submits the correct variant even if the visual state couldn't refresh).
 * Silent console.warn in dev only.
 */
(function () {
  'use strict';

  if (window.HydrusVariantReactivity) return;

  window.HydrusVariantReactivity = {
    /**
     * Wire up a PDP section for variant reactivity.
     * @param {Object} config
     * @param {string} config.sectionId - Shopify section.id
     * @param {string} config.productUrl - product.url for the section rendering fetch
     * @param {string} config.reactiveZoneId - id of the DOM element wrapping the reactive region
     * @param {Object<string, number>} config.variantMap - map of variant.title → variant.id
     */
    init: function (config) {
      var reactiveZone = document.getElementById(config.reactiveZoneId);
      if (!reactiveZone) return;

      var form = reactiveZone.closest('form');
      if (!form) return;

      var lastVariantId = null;

      form.addEventListener('change', function (e) {
        var target = e.target;
        if (!target || target.type !== 'radio') return;
        if (typeof target.name !== 'string' || target.name.indexOf('options[') !== 0) return;

        var selectedValues = [];
        form.querySelectorAll('input[type="radio"][name^="options["]:checked').forEach(function (input) {
          selectedValues.push(input.value);
        });
        var key = selectedValues.join(' / ');

        var variantId = config.variantMap[key];
        if (!variantId) return;
        if (variantId === lastVariantId) return;
        lastVariantId = variantId;

        var hiddenIdInput = form.querySelector('input[name="id"]');
        if (hiddenIdInput) hiddenIdInput.value = String(variantId);

        var fetchUrl = config.productUrl + '?variant=' + variantId + '&section_id=' + config.sectionId;

        fetch(fetchUrl, { headers: { Accept: 'text/html' } })
          .then(function (response) {
            if (!response.ok) throw new Error('Section fetch returned ' + response.status);
            return response.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var incoming = parsed.getElementById(config.reactiveZoneId);
            if (!incoming) return;
            reactiveZone.innerHTML = incoming.innerHTML;
          })
          .catch(function (err) {
            if (window.console && typeof window.console.warn === 'function') {
              window.console.warn('[Hydrus] variant reactivity fetch failed:', err);
            }
          });
      });
    }
  };
})();
